import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { Button, Grid, Icon, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Box, Container } from '@mui/system';

import Address from 'Lib/components/common/Address';
import Card from 'Lib/components/common/Card';
import Loading from 'Lib/components/common/Loading';
import FieldArray from 'Lib/components/form/FieldArray';
import Form from 'Lib/components/form/Form';
import TextField from 'Lib/components/form/TextField';
import StartedApplicationStatuses from 'Lib/constants/screening/startedApplicationStatuses';
import useApi from 'Lib/hooks/useApi';
import isEmpty from 'Lib/utilities/isEmpty';

const APPLICANT = { firstName: null, lastName: null, email: null };

const LinkButton = ({ to, children, sx, ...rest }) => {
    return (
        // Need to enforce color with !important here because blue color for link overrides sx prop
        <Button component={Link} to={to} variant="primary" sx={{ color: 'white !important', ...sx }} {...rest}>
            {children}
        </Button>
    );
};

export const InviteForm = ({ applicationGroup = {}, form }) => (
    <FieldArray
        name="invitees"
        render={arrayHelper => (
            <>
                {form.values.invitees.map((__, index) => (
                    <Grid key={index} container spacing={2}>
                        <Grid item>
                            <TextField id={`invitees[${index}].firstName`} name={`invitees[${index}].firstName`} label="First Name" isRequired />
                        </Grid>
                        <Grid item>
                            <TextField id={`invitees[${index}].lastName`} name={`invitees[${index}].lastName`} label="Last Name" isRequired />
                        </Grid>
                        <Grid item>
                            <TextField id={`invitees[${index}].email`} name={`invitees[${index}].email`} label="Email Address" isRequired />
                        </Grid>
                        {(applicationGroup.group || index > 0) && (
                            <Grid item style={{ alignSelf: 'center' }}>
                                <Tooltip arrow title="Remove applicant">
                                    <IconButton variant="outlined" onClick={() => arrayHelper.remove(index)} sx={{ color: 'common.black', mt: 6, ml: 2 }}>
                                        <span className="material-symbols-rounded">remove</span>
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        )}
                    </Grid>
                ))}
                <Button
                    variant="outlined"
                    startIcon={<Icon>add</Icon>}
                    onClick={() => arrayHelper.push(APPLICANT)}
                    sx={{ borderColor: 'common.black', color: 'common.black', textTransform: 'inherit' }}
                >
                    Add more applicants
                </Button>
            </>
        )}
    />
);

export default function ApplicationSuccess({ address, applicant, createApplicationGroup, getApplicationGroup, handlePromise, notify }) {
    const [loading, setLoading] = useState(false);
    const [applicationGroup, setApplicationGroup] = useState({ group: null, invitees: [] });

    const fetchApplicationGroup = useApi(getApplicationGroup);
    const sendInvitations = useApi(createApplicationGroup);

    const otherInvitees = applicationGroup.invitees.filter(invitee => !invitee.isCurrentUser);
    const hasExistingApplicants = !isEmpty(otherInvitees);

    useEffect(() => {
        if (applicant?.applicationID && fetchApplicationGroup) {
            setLoading(true);

            fetchApplicationGroup(applicant?.applicationID)
                .then(response => {
                    setApplicationGroup(response.data);
                    setLoading(false);
                })
                .catch(() => {
                    setLoading(false);
                });
        }
    }, []);

    const onSubmit = (values, form) => {
        handlePromise(form, [sendInvitations(values)])
            .then(response => {
                notify({ success: 'Successfully sent invitations' });
                setApplicationGroup(response.data);
                form.resetForm();
            })
            .catch(error => {
                notify({ error });
            });
    };

    const Applicants = ({ form }) => {
        const CurrentApplicants = () => (
            <Box sx={{ maxWidth: '400px', mb: 6 }}>
                {otherInvitees.map((invitee, index) => {
                    const statusID = invitee.statusID;
                    const icon = StartedApplicationStatuses.getProperty(statusID, 'icon');
                    const color = StartedApplicationStatuses.getProperty(statusID, 'color');

                    return (
                        <Box key={index} display="flex" justifyContent="space-between">
                            <Typography>
                                {invitee.firstName} {invitee.lastName}
                            </Typography>
                            <Stack direction="row">
                                {icon && <Icon sx={{ color: color, fontWeight: 'bold', marginRight: '4px' }}>{icon}</Icon>}
                                <Typography>{StartedApplicationStatuses.getName(statusID)}</Typography>
                            </Stack>
                        </Box>
                    );
                })}
            </Box>
        );

        return (
            <>
                {/* Show existing applicants associated to the submitted application */}
                {hasExistingApplicants ? <CurrentApplicants /> : null}
                {/* Sending of invitations (name and email) */}
                <InviteForm applicationGroup={applicationGroup} form={form} />
            </>
        );
    };

    return (
        <Stack p={3} direction='column'>
            <Card title="Success!">
                Thank you for your submission to <Address displayInline value={address} />!
                <br />
                You will be contacted as soon as we have finished reviewing your application.
            </Card>

            <Card title={loading ? '' : hasExistingApplicants ? 'Other Applicants' : 'Invite Other Applicants'}>
                {loading ? (
                    <Container>
                        <Loading />
                    </Container>
                ) : (
                    <Form
                        enableReinitialize
                        initialValues={{
                            applicantID: applicant?.applicantID,
                            invitees: applicationGroup.group ? [] : [APPLICANT]
                        }}
                        onSubmit={onSubmit}
                        render={form => {
                            // Checks is any value in the form is empty. If so, disables the submit button since all fields are required
                            const isSubmitDisabled = form.values.invitees.some(invitee => Object.values(invitee).some(value => !value)) || isEmpty(form.values.invitees);

                            return (
                                <form onSubmit={form.handleSubmit}>
                                    <Applicants form={form} />
                                    <Box sx={{ pt: 10 }}>
                                        <LoadingButton loading={form.isSubmitting} disabled={isSubmitDisabled} variant="contained" color="success" type="submit">
                                            Send Invitations
                                        </LoadingButton>
                                    </Box>
                                </form>
                            );
                        }}
                    />
                )}
            </Card>

            <LinkButton to="/" sx={{ minWidth: '300px', alignSelf: 'center' }}>
                Go To Dashboard
            </LinkButton>
        </Stack>
    );
}
